import React, { type ReactNode, createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import type { AnalysisStatus } from '../types';
import { useNotifications } from './NotificationContext';
import { parseAnalysisError } from '../lib/analysisErrors';

interface UploadState {
    id: string; // candidate id
    filename: string;
    status: AnalysisStatus;
    stepIndex: number; // 0 to 3
    error?: string;
    startedAt: number; // epoch ms — borne la durée d'attente (cf. ANALYSIS_TIMEOUT_MS)
}

interface MissionUploadTracker {
    id: string;       // local UI key (timestamp)
    title: string;
    filename: string; // uploaded file name — used to match the Supabase row (same as candidate pattern)
    status: string;
    dbId: string | null;
    isClosing: boolean;
}

interface ProcessingContextType {
    activeUploads: Map<string, UploadState>;
    startMonitoring: (candidateId: string, filename: string, file?: File) => void;
    dismissUpload: (candidateId: string) => void;
    /** Fichier d'origine gardé en mémoire pour un « Relancer » d'un seul clic.
     *  `undefined` après un rechargement de page : un File n'est pas sérialisable,
     *  il ne peut pas suivre `activeUploads` dans le localStorage. */
    getRetryFile: (candidateId: string) => File | undefined;
    activeMissionUploads: MissionUploadTracker[];
    startMissionMonitoring: (uiId: string, title: string, filename: string) => void;
    dismissMissionUpload: (uiId: string) => void;
    updateMissionExecId: (uiId: string, execId: string) => void;
    updateMissionDbId: (uiId: string, dbId: string) => void;
    missionCompletionTick: number;
}

const ProcessingContext = createContext<ProcessingContextType | undefined>(undefined);

// Status mapping to steps
const STATUS_STEPS: Record<AnalysisStatus, number> = {
    'received': 0, // "Lancement de l'analyse" 
    'analyzing_doc': 1, // "Analyse type de document"
    'analyzing_agents': 2, // "Analyse des Agents"
    'completed': 3, // "Analyse complete"
    'duplicate': 3, // "Candidat existant"
    'error': -1
};

// L'edge function d'analyse est tuée à 150 s d'horloge murale. Si elle meurt
// avant d'avoir écrit son statut d'erreur, la fiche reste sur un statut
// intermédiaire et cette carte tournerait indéfiniment (incident du
// 2026-08-17). Au-delà de ce délai — large devant les 150 s — on tranche côté UI
// sans attendre le balayage serveur.
const ANALYSIS_TIMEOUT_MS = 4 * 60 * 1000;

// Helper to normalize status strings to internal AnalysisStatus values
const normalizeStatus = (status: string): AnalysisStatus => {
    if (!status) return 'received';

    // Normalize string to handle case sensitivity and trimming
    const s = status.toLowerCase().trim();

    if (s.includes('error') || s.includes('erreur')) return 'error';
    if (s.includes('duplicate') || s.includes('existant') || s.includes('doublon')) return 'duplicate';
    if (s.includes('complete') || s.includes('terminé') || s.includes('termine') || s.includes('disponible')) return 'completed';
    if (s.includes('agent') || s.includes('ia')) return 'analyzing_agents';
    if (s.includes('document') || s.includes('type')) return 'analyzing_doc';
    if (s.includes('reçu') || s.includes('recu') || s.includes('lancement')) return 'received';

    // Default fallback
    return 'received';
};

export const ProcessingProvider = ({ children, onNavigateToCandidate, onNavigateToMission }: { children: ReactNode, onNavigateToCandidate?: (id: string) => void, onNavigateToMission?: (id: string) => void }) => {
    // Track which candidate IDs have already triggered a "completed" notification
    // to prevent duplicate toasts from both the realtime subscription and the polling fallback
    const notifiedCompletedRef = useRef(new Set<string>());

    // Pierres tombales de dédup déjà supprimées — évite un second DELETE au re-render
    const consumedDuplicatesRef = useRef(new Set<string>());

    // Lazy initialization to prevent overwriting localStorage on mount
    const [activeUploads, setActiveUploads] = useState<Map<string, UploadState>>(() => {
        try {
            const saved = localStorage.getItem('activeUploads');
            if (saved) {
                // Les entrées d'avant l'introduction de `startedAt` n'en ont pas :
                // on les repart à maintenant plutôt que de les faire expirer
                // aussitôt (ce qui lèverait un toast d'erreur au chargement).
                const entries: Array<[string, UploadState]> = JSON.parse(saved);
                return new Map(entries.map(([k, v]) => [k, { ...v, startedAt: v.startedAt ?? Date.now() }]));
            }
        } catch (e) {
            console.error("Failed to load active uploads", e);
        }
        return new Map();
    });

    const [activeMissionUploads, setActiveMissionUploads] = useState<MissionUploadTracker[]>(() => {
        try {
            const saved = localStorage.getItem('activeMissionUploads');
            if (saved) {
                const parsed: MissionUploadTracker[] = JSON.parse(saved);
                // On mount: discard zombie trackers (unbound = never matched a DB row, from old/failed sessions)
                // Only keep trackers that: (a) are already bound to a real DB id, AND (b) have not finished
                const alive = parsed.filter(u => u.dbId !== null && !u.isClosing);

                return alive;
            }
        } catch (e) {
            console.error("Failed to load active mission uploads", e);
        }
        return [];
    });

    const [missionCompletionTick, setMissionCompletionTick] = useState(0);

    const activeUploadsRef = useRef(activeUploads);
    const activeMissionUploadsRef = useRef(activeMissionUploads);
    const { notify } = useNotifications();

    useEffect(() => {
        activeUploadsRef.current = activeUploads;
        const asArray = Array.from(activeUploads.entries());
        localStorage.setItem('activeUploads', JSON.stringify(asArray));
    }, [activeUploads]);

    useEffect(() => {
        activeMissionUploadsRef.current = activeMissionUploads;
        localStorage.setItem('activeMissionUploads', JSON.stringify(activeMissionUploads));
    }, [activeMissionUploads]);

    // Le CV déposé, gardé le temps de la session. Sans lui, « Relancer » sur une
    // carte d'erreur ne peut rien renvoyer : le serveur n'archive pas le fichier
    // source, et le mode `reanalyze` repart d'un `original_cv_text` qui vaut
    // « Analyzed via Vision API » (23 caractères) sur tout PDF.
    const retryFilesRef = useRef(new Map<string, File>());

    const getRetryFile = useCallback((candidateId: string) => retryFilesRef.current.get(String(candidateId)), []);

    const startMonitoring = useCallback((candidateId: string, filename: string, file?: File) => {
        if (file) retryFilesRef.current.set(String(candidateId), file);

        setActiveUploads(prev => {
            const newMap = new Map(prev);
            newMap.set(String(candidateId), {
                id: String(candidateId),
                filename,
                status: 'received',
                stepIndex: 0,
                startedAt: Date.now()
            });
            return newMap;
        });
    }, []);

    const dismissUpload = useCallback((candidateId: string) => {
        retryFilesRef.current.delete(String(candidateId));
        setActiveUploads(prev => {
            const newMap = new Map(prev);
            newMap.delete(String(candidateId));
            return newMap;
        });
    }, []);

    const startMissionMonitoring = useCallback((uiId: string, title: string, filename: string) => {

        setActiveMissionUploads(prev => [
            ...prev,
            { id: uiId, title, filename, status: 'initialisation', dbId: null, isClosing: false }
        ]);
    }, []);

    const dismissMissionUpload = useCallback((uiId: string) => {
        setActiveMissionUploads(prev => prev.filter(item => item.id !== uiId));
    }, []);

    const updateMissionExecId = useCallback((uiId: string, execId: string) => {

        setActiveMissionUploads(prev => prev.map(u =>
            u.id === uiId ? { ...u, execId } : u
        ));
    }, []);

    const updateMissionDbId = useCallback((uiId: string, dbId: string) => {

        setActiveMissionUploads(prev => prev.map(u =>
            u.id === uiId ? { ...u, dbId } : u
        ));
    }, []);

    // Realtime Subscription
    useEffect(() => {

        const channel = supabase
            .channel('candidate_updates_global')
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'candidates',
                },
                (payload) => {
                    const updatedCandidate = payload.new as any;
                    const idString = String(updatedCandidate.id);

                    // Normalize the status received from Supabase realtime
                    const rawStatus: string = updatedCandidate.status || '';
                    const newStatus = normalizeStatus(rawStatus);
                    const errorDetails: string | undefined = updatedCandidate.error_details;
                    const filename: string | undefined = updatedCandidate.filename;

                    // Logic:
                    // 1. Try to find by ID
                    // 2. If failure, try to find by filename (only if status is 'received' or similar)

                    let targetId = idString;
                    let found = activeUploadsRef.current.has(idString);
                    let shouldUpdateId = false;

                    if (!found && filename) {
                        // Scan active uploads for a matching filename that has a "bad" ID (e.g. {{...}})
                        for (const [key, value] of activeUploadsRef.current.entries()) {
                            if (value.filename === filename) {
                                targetId = key; // We found the old entry key
                                found = true;
                                shouldUpdateId = true; // We need to fix the ID in our map
                                break;
                            }
                        }
                    }

                    // Check if we are monitoring this ID (or found via filename)
                    if (found) {
                        const current = activeUploadsRef.current.get(targetId);

                        // Prevent unnecessary updates
                        if (current && current.status === newStatus && current.error === errorDetails) {
                            return; // No change
                        }

                        setActiveUploads(prev => {
                            const newMap = new Map(prev);

                            // If we found a match by filename, we swap the key
                            if (shouldUpdateId) {
                                const oldEntry = newMap.get(targetId);
                                if (oldEntry) {
                                    newMap.delete(targetId);
                                    newMap.set(idString, { ...oldEntry, id: idString }); // Update ID
                                }
                                targetId = idString; // Continue with new ID
                            }

                            const currentInner = newMap.get(targetId);
                            if (!currentInner) return prev; // Should not happen

                            // Toast on completion (deduplicated via notifiedCompletedRef)
                            if ((newStatus === 'completed' || newStatus === 'duplicate') && currentInner.status !== 'completed' && currentInner.status !== 'duplicate' && !notifiedCompletedRef.current.has(idString)) {
                                notifiedCompletedRef.current.add(idString);
                                
                                const isDuplicate = newStatus === 'duplicate';
                                const finalId = isDuplicate && errorDetails ? errorDetails : idString;
                                
                                notify('success', isDuplicate ? `Profil existant mis à jour : ${currentInner.filename}` : `Analyse terminée : ${currentInner.filename}`, {
                                    description: isDuplicate ? "Le candidat était déjà dans la base." : "Le profil est maintenant disponible.",
                                    action: {
                                        label: "Voir",
                                        path: `/talents/${finalId}`,
                                        onClick: () => {
                                            if (onNavigateToCandidate) {
                                                onNavigateToCandidate(finalId);
                                            }
                                        }
                                    }
                                });

                                // Auto-dismiss after 5 seconds
                                setTimeout(() => {
                                    dismissUpload(targetId);
                                }, 5000);
                            }

                            // Toast on error
                            if (newStatus === 'error' && (currentInner as UploadState)?.status !== 'error') {
                                {
                                    const info = parseAnalysisError(errorDetails);
                                    notify('error', `${info.title} — ${currentInner.filename}`, {
                                        description: info.description,
                                    });
                                }
                            }

                            newMap.set(targetId, {
                                ...currentInner,
                                status: newStatus,
                                stepIndex: STATUS_STEPS[newStatus] || 0,
                                error: errorDetails
                            } as UploadState);
                            return newMap;
                        });
                    }
                }
            )
            .subscribe((status) => {
                if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
                    console.warn("Realtime connection issues detected. Polling will take over.");
                }
            });

        return () => {
            supabase.removeChannel(channel);
        };
    }, []);

    // ─── Consommation des pierres tombales de dédup ─────────────────────────────
    // Sur un doublon, l'edge function laisse la ligne temporaire en `duplicate` avec
    // l'id du profil cible dans `error_details`, et c'est au front de la supprimer
    // une fois qu'il l'a lue. La supprimer côté serveur créait une course : elle
    // disparaissait avant que le sondage (5 s) ne l'ait vue, et la carte restait
    // bloquée. Garde-fou : on ne supprime qu'une ligne qui pointe explicitement vers
    // un AUTRE profil, pour ne jamais toucher un profil réel.
    useEffect(() => {
        activeUploads.forEach(upload => {
            if (upload.status !== 'duplicate') return;
            if (upload.id.startsWith('temp-') || consumedDuplicatesRef.current.has(upload.id)) return;

            const target = upload.error;
            if (!target || target === upload.id) return;

            consumedDuplicatesRef.current.add(upload.id);
            supabase.from('candidates').delete().eq('id', Number(upload.id)).then(({ error }) => {
                if (error) console.warn('Suppression de la pierre tombale de dédup échouée', error);
            });
        });
    }, [activeUploads]);

    // POLLING FALLBACK (Backup system if WebSockets fail or IDs are messed up)
    useEffect(() => {
        const interval = setInterval(async () => {
            if (activeUploadsRef.current.size === 0) return;

            // Extract filenames we are waiting for
            // 'duplicate' est un état terminal (STATUS_STEPS = 3) au même titre que
            // 'completed' : le sonder encore relancerait le timeout ci-dessous sur une
            // analyse pourtant réussie.
            const uploadsToPoll = Array.from(activeUploadsRef.current.values())
                .filter(u => u.status !== 'completed' && u.status !== 'error' && u.status !== 'duplicate');

            if (uploadsToPoll.length === 0) return;

            // === TIMEOUT: le serveur ne rendra plus de verdict ===
            // Les notifications sont émises hors du updater de state : appeler
            // notify() dedans le ferait tirer deux fois en mode strict.
            const timedOut = uploadsToPoll.filter(u => Date.now() - u.startedAt > ANALYSIS_TIMEOUT_MS);
            if (timedOut.length > 0) {
                setActiveUploads(prev => {
                    const newMap = new Map(prev);
                    timedOut.forEach(u => {
                        const entry = newMap.get(u.id);
                        if (!entry) return;
                        newMap.set(u.id, {
                            ...entry,
                            status: 'error',
                            stepIndex: STATUS_STEPS['error'],
                            error: "Analyse interrompue côté serveur — re-déposez le fichier."
                        });
                    });
                    return newMap;
                });
                // Le filtre uploadsToPoll exclut déjà les 'error' : pas de re-notification
                // au tick suivant.
                timedOut.forEach(u => {
                    notify('error', `Analyse interrompue : ${u.filename}`, {
                        description: "Le serveur n'a pas rendu de résultat dans le délai imparti. Re-déposez le fichier."
                    });
                });
            }

            // === POLL BY ID (handles existing candidates being re-analyzed) ===
            // Filename polling fails for existing candidates because the DB stores the OLD filename.
            // Querying by known ID is authoritative and avoids this mismatch.
            const knownIds = uploadsToPoll
                .map(u => u.id)
                .filter(id => !id.startsWith('temp-'));

            if (knownIds.length > 0) {
                const { data: byIdData } = await supabase
                    .from('candidates')
                    .select('id, status, updated_at, error_details')
                    .in('id', knownIds.map(Number));

                if (byIdData) {
                    byIdData.forEach((candidate: any) => {
                        const realId = String(candidate.id);
                        const newStatus = normalizeStatus(candidate.status || '');
                        const errorDetails = candidate.error_details;

                        // Only process if recently updated — prevents stale "completed" status
                        // from the PREVIOUS analysis from triggering an immediate false completion.
                        const updatedAt = candidate.updated_at;
                        if (updatedAt) {
                            const diffMinutes = (new Date().getTime() - new Date(updatedAt).getTime()) / (1000 * 60);
                            if (diffMinutes > 10) return;
                        }

                        const current = activeUploadsRef.current.get(realId);
                        if (!current || (current.status === newStatus && current.error === errorDetails)) return;



                        setActiveUploads(prev => {
                            const newMap = new Map(prev);
                            const entry = newMap.get(realId);
                            if (!entry) return newMap;

                            if ((newStatus === 'completed' || newStatus === 'duplicate') && entry.status !== 'completed' && entry.status !== 'duplicate' && !notifiedCompletedRef.current.has(realId)) {
                                notifiedCompletedRef.current.add(realId);
                                
                                const isDuplicate = newStatus === 'duplicate';
                                const finalId = isDuplicate && errorDetails ? errorDetails : realId;
                                
                                notify('success', isDuplicate ? `Profil existant mis à jour : ${entry.filename}` : `Analyse terminée : ${entry.filename}`, {
                                    description: isDuplicate ? "Le candidat était déjà dans la base." : "Le profil est maintenant disponible.",
                                    action: {
                                        label: "Voir",
                                        path: `/talents/${finalId}`,
                                        onClick: () => { if (onNavigateToCandidate) onNavigateToCandidate(finalId); }
                                    }
                                });
                                setTimeout(() => { dismissUpload(realId); }, 5000);
                            }
                            if (newStatus === 'error' && entry.status !== 'error') {
                                {
                                    const info = parseAnalysisError(errorDetails);
                                    notify('error', `${info.title} — ${entry.filename}`, { description: info.description });
                                }
                            }

                            newMap.set(realId, {
                                ...entry,
                                status: newStatus,
                                stepIndex: STATUS_STEPS[newStatus] || 0,
                                error: errorDetails
                            } as UploadState);
                            return newMap;
                        });
                    });
                }
            }

            // === POLL BY FILENAME (for new candidates where ID was initially unknown) ===
            const filenames = uploadsToPoll.map(u => u.filename);

            const { data, error } = await supabase
                .from('candidates')
                .select('*')
                .in('filename', filenames);

            if (data && data.length > 0) {
                data.forEach((candidate: any) => {
                    const newStatus = normalizeStatus(candidate.status || '');
                    const errorDetails = candidate.error_details;

                    // Find matching upload locally
                    // We match by filename to be safe
                    let targetId: string | null = null;
                    for (const [key, val] of activeUploadsRef.current.entries()) {
                        if (val.filename === candidate.filename) {
                            // STRICT POLL CHECK:
                            // Only match if the candidate was updated recently (e.g. within last 10 minutes)
                            // This prevents latching onto old, existing profiles when a new upload fails (duplicate key error)
                            const updatedAt = candidate.updated_at || candidate.created_at;
                            if (updatedAt) {
                                const diffMinutes = (new Date().getTime() - new Date(updatedAt).getTime()) / (1000 * 60);
                                if (diffMinutes > 10) {

                                    continue;
                                }
                            }

                            targetId = key;
                            break;
                        }
                    }

                    if (targetId) {
                        const current = activeUploadsRef.current.get(targetId);
                        // Only update if changed
                        if (current && (current.status !== newStatus || current.error !== errorDetails)) {


                            setActiveUploads(prev => {
                                const newMap = new Map(prev);

                                // Fix ID if needed
                                const realId = String(candidate.id);
                                if (targetId && targetId !== realId) {
                                    const old = newMap.get(targetId);
                                    if (old) {
                                        newMap.delete(targetId);
                                        newMap.set(realId, { ...old, id: realId } as UploadState);
                                        targetId = realId;
                                    }
                                }

                                if (!targetId) return newMap;

                                const entry = newMap.get(targetId);
                                if (entry) {
                                    // Trigger toasts (deduplicated via notifiedCompletedRef)
                                    if ((newStatus === 'completed' || newStatus === 'duplicate') && entry.status !== 'completed' && entry.status !== 'duplicate' && !notifiedCompletedRef.current.has(realId)) {
                                        notifiedCompletedRef.current.add(realId);
                                        
                                        const isDuplicate = newStatus === 'duplicate';
                                        const finalId = isDuplicate && errorDetails ? errorDetails : realId;

                                        notify('success', isDuplicate ? `Profil existant mis à jour : ${entry.filename}` : `Analyse terminée : ${entry.filename}`, {
                                            description: isDuplicate ? "Le candidat était déjà dans la base." : "Le profil est maintenant disponible.",
                                            action: {
                                                label: "Voir",
                                                path: `/talents/${finalId}`,
                                                onClick: () => {
                                                    if (onNavigateToCandidate) {
                                                        onNavigateToCandidate(finalId);
                                                    }
                                                }
                                            }
                                        });

                                        // Auto-dismiss after 5 seconds
                                        setTimeout(() => {
                                            dismissUpload(targetId!);
                                        }, 5000);
                                    }
                                    if (newStatus === 'error' && entry.status !== 'error') {
                                        {
                                            const info = parseAnalysisError(errorDetails);
                                            notify('error', `${info.title} — ${entry.filename}`, { description: info.description });
                                        }
                                    }

                                    newMap.set(targetId, {
                                        ...entry,
                                        status: newStatus,
                                        stepIndex: STATUS_STEPS[newStatus] || 0,
                                        error: errorDetails
                                    } as UploadState);
                                }
                                return newMap;
                            });
                        }
                    }
                });
            }

        }, 5000); // Poll every 5 seconds (candidates only)

        return () => clearInterval(interval);
    }, []);

    // ─── DEDICATED MISSION POLLING (separate from candidate polling) ────────────
    useEffect(() => {
        const missionInterval = setInterval(async () => {
            const missionQueue = activeMissionUploadsRef.current;
            if (missionQueue.length === 0) return;

            const pendingMissions = missionQueue.filter(u => !u.isClosing);
            if (pendingMissions.length === 0) return;

            const DONE_STATUSES = ['prospection', 'mission existante', 'mission_existante'];
            const ERROR_STATUSES = ['error', 'erreur'];

            // 1. Bound missions: poll by DB id directly
            const boundIds = pendingMissions.filter(u => u.dbId).map(u => u.dbId!);
            if (boundIds.length > 0) {
                const { data: boundRows } = await supabase
                    .from('missions')
                    .select('id, status, updated_at, error_details')
                    .in('id', boundIds.map(Number));

                if (boundRows) {
                    (boundRows as any[]).forEach((m: any) => {
                        const dbIdStr = String(m.id);
                        const newStatus = m.status || 'initialisation';
                        const newStatusLower = newStatus.toLowerCase().trim();
                        const errorDetails: string | undefined = m.error_details;

                        const tracker = activeMissionUploadsRef.current.find(u => u.dbId === dbIdStr);
                        if (!tracker || tracker.isClosing || tracker.status === newStatus) return;

                        const isDone = DONE_STATUSES.includes(newStatusLower);
                        const isError = ERROR_STATUSES.includes(newStatusLower);

                        if (isDone) {
                            notify('success', `Mission analysée : ${tracker.title}`, {
                                description: newStatusLower === 'prospection'
                                    ? 'La mission est disponible dans le Kanban.'
                                    : 'Mission déjà connue — doublon détecté.',
                                action: newStatusLower === 'prospection' ? {
                                    label: 'Voir',
                                    path: `/missions/${dbIdStr}`,
                                    onClick: () => { if (onNavigateToMission) onNavigateToMission(dbIdStr); }
                                } : undefined
                            });
                            setMissionCompletionTick(t => t + 1);
                        }

                        if (isError) {
                            const isGeminiDown = errorDetails === 'GEMINI_UNAVAILABLE';
                            notify('error', `Analyse échouée : ${tracker.title}`, {
                                description: isGeminiDown
                                    ? 'Gemini est momentanément indisponible. Relancez l\'analyse dans quelques minutes.'
                                    : errorDetails || 'Une erreur est survenue.',
                                action: {
                                    label: 'Voir la mission',
                                    path: `/missions/${dbIdStr}`,
                                    onClick: () => { if (onNavigateToMission) onNavigateToMission(dbIdStr); }
                                }
                            });
                        }

                        setActiveMissionUploads(curr => curr.map(u =>
                            u.id === tracker.id
                                ? { ...u, status: newStatus, isClosing: isDone || isError }
                                : u
                        ));
                    });
                }
            }

            // 2. Unbound missions: find the most recently updated mission (last 10 min)
            //    No active-status filter — edge function can finish before the first 3s poll fires
            const hasUnbound = pendingMissions.some(u => !u.dbId);
            if (hasUnbound) {
                const tenMinsAgo = new Date(Date.now() - 10 * 60 * 1000).toISOString();
                const { data: freshRows } = await supabase
                    .from('missions')
                    .select('id, status, updated_at, error_details')
                    .gte('updated_at', tenMinsAgo)
                    .order('updated_at', { ascending: false })
                    .limit(5);

                if (freshRows) {
                    (freshRows as any[]).forEach((m: any) => {
                        const dbIdStr = String(m.id);
                        const newStatus = m.status || 'initialisation';
                        const newStatusLower = newStatus.toLowerCase().trim();
                        const errorDetails: string | undefined = m.error_details;

                        const curr = activeMissionUploadsRef.current;
                        if (curr.find(u => u.dbId === dbIdStr)) return;
                        const unbound = curr.find(u => !u.dbId && !u.isClosing);
                        if (!unbound) return;

                        const isDone = DONE_STATUSES.includes(newStatusLower);
                        const isError = ERROR_STATUSES.includes(newStatusLower);

                        if (isDone) {
                            notify('success', `Mission analysée : ${unbound.title}`, {
                                description: newStatusLower === 'prospection'
                                    ? 'La mission est disponible dans le Kanban.'
                                    : 'Mission déjà connue — doublon détecté.',
                                action: newStatusLower === 'prospection' ? {
                                    label: 'Voir',
                                    path: `/missions/${dbIdStr}`,
                                    onClick: () => { if (onNavigateToMission) onNavigateToMission(dbIdStr); }
                                } : undefined
                            });
                            setMissionCompletionTick(t => t + 1);
                        }

                        if (isError) {
                            const isGeminiDown = errorDetails === 'GEMINI_UNAVAILABLE';
                            notify('error', `Analyse échouée : ${unbound.title}`, {
                                description: isGeminiDown
                                    ? 'Gemini est momentanément indisponible. Relancez l\'analyse dans quelques minutes.'
                                    : errorDetails || 'Une erreur est survenue.',
                                action: {
                                    label: 'Voir la mission',
                                    path: `/missions/${dbIdStr}`,
                                    onClick: () => { if (onNavigateToMission) onNavigateToMission(dbIdStr); }
                                }
                            });
                        }

                        setActiveMissionUploads(prev => prev.map(u =>
                            u.id === unbound.id
                                ? { ...u, dbId: dbIdStr, status: newStatus, isClosing: isDone || isError }
                                : u
                        ));
                    });
                }
            }
        }, 5000);

        return () => clearInterval(missionInterval);
    }, []);

    return (
        <ProcessingContext.Provider value={{ activeUploads, startMonitoring, dismissUpload, getRetryFile, activeMissionUploads, startMissionMonitoring, dismissMissionUpload, updateMissionExecId, updateMissionDbId, missionCompletionTick }}>
            {children}
        </ProcessingContext.Provider>
    );
};

export const useProcessing = () => {
    const context = useContext(ProcessingContext);
    if (context === undefined) {
        throw new Error('useProcessing must be used within a ProcessingProvider');
    }
    return context;
};
